import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Page,
	Layout,
	Card,
	Text,
	DataTable,
	Banner,
	SkeletonPage,
	SkeletonBodyText,
	SkeletonDisplayText,
	Badge,
	BlockStack,
	InlineStack,
	InlineGrid,
	Button,
	Divider,
	EmptyState,
	Tooltip,
	Icon,
	Box,
} from "@shopify/polaris";
import { AlertCircleIcon, CheckCircleIcon } from "@shopify/polaris-icons";
import { authenticatedFetch } from "../api";
import type { DashboardData, RunEntry } from "../types";
import { ProductCounts } from "../components/ProductCounts";
import { EntitlementBanner, useUsage } from "../components/EntitlementBanner";

const POLL_INTERVAL_MS = 30_000;

function relativeTime(dateStr: string): string {
	const normalized = dateStr.replace(" - ", "T");
	const then = new Date(normalized);
	if (isNaN(then.getTime())) return "";
	const diffMs = Date.now() - then.getTime();
	const diffMin = Math.floor(diffMs / 60_000);
	if (diffMin < 1) return "Just now";
	if (diffMin < 60) return `${diffMin} min ago`;
	const diffHours = Math.floor(diffMin / 60);
	if (diffHours < 24) return `${diffHours}h ago`;
	const diffDays = Math.floor(diffHours / 24);
	return `${diffDays}d ago`;
}

function formatDate(dateStr: string | null): string {
	if (!dateStr) return "—";
	try {
		const normalized = dateStr.replace(" - ", "T");
		const parsed = new Date(normalized);
		if (isNaN(parsed.getTime())) return dateStr;
		return parsed.toLocaleString("en-GB", {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	} catch {
		return dateStr;
	}
}

function formatNumber(n: number | undefined): string {
	return (n ?? 0).toLocaleString("en-GB");
}

function localeName(code: string): string {
	try {
		return new Intl.DisplayNames(["en"], { type: "language" }).of(code) ?? code;
	} catch {
		return code;
	}
}

function StatCard({ label, value }: { label: string; value: string }) {
	return (
		<Card>
			<BlockStack gap="200">
				<Text variant="bodySm" as="p" tone="subdued">
					{label}
				</Text>
				<Text variant="headingLg" as="p">
					{value}
				</Text>
			</BlockStack>
		</Card>
	);
}

const RUN_TRANSLATION_TOOLTIP =
	"Open the manual translation screen. A run translates products queued with the translate-pending tag, up to the product limit you choose before starting.";

const PLAN_TARGET_LANGUAGE_LIMITS: Record<string, number> = {
	Starter: 2,
	Growth: 3,
	Enterprise: 5,
};

function targetLanguageLimit(planName?: string | null): number | null {
	if (!planName) return null;
	return PLAN_TARGET_LANGUAGE_LIMITS[planName] ?? null;
}

function ChecklistItem({
	complete,
	label,
	detail,
}: {
	complete: boolean;
	label: string;
	detail: string;
}) {
	return (
		<InlineStack gap="200" blockAlign="start" wrap={false}>
			<Box>
				<Icon
					source={complete ? CheckCircleIcon : AlertCircleIcon}
					tone={complete ? "success" : "subdued"}
				/>
			</Box>
			<BlockStack gap="050">
				<Text as="p" variant="bodyMd" fontWeight="semibold">
					{label}
				</Text>
				<Text as="p" variant="bodySm" tone="subdued">
					{detail}
				</Text>
			</BlockStack>
		</InlineStack>
	);
}

function ConfigurationOnboarding({
	engine,
	sourceLocale,
	targetLocales,
	planName,
}: {
	engine: string;
	sourceLocale: string;
	targetLocales: string[];
	planName?: string | null;
}) {
	const targetLimit = targetLanguageLimit(planName);
	const hasEngine = engine !== "—";
	const hasSourceLocale = Boolean(sourceLocale);
	const hasTargetLocales = targetLocales.length > 0;
	const targetProgress = targetLimit
		? `${Math.min(targetLocales.length, targetLimit)} of ${targetLimit} target languages selected`
		: `${targetLocales.length} target languages selected`;

	return (
		<Layout.Section>
			<Card>
				<BlockStack gap="500">
					<InlineStack align="space-between" blockAlign="start" gap="400" wrap>
						<BlockStack gap="200">
							<Text variant="headingMd" as="h2">
								Finish your translation setup
							</Text>
							<Text as="p" variant="bodyMd" tone="subdued">
								Configuration tells Duxly which language to translate from, which
								storefront languages to update, and which engine should handle the
								translation work.
							</Text>
						</BlockStack>
						<Button url="/configuration" variant="primary">
							Configure
						</Button>
					</InlineStack>

					<InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
						<ChecklistItem
							complete={hasSourceLocale}
							label="Source language"
							detail={
								hasSourceLocale
									? `${localeName(sourceLocale)} is used as the original product language.`
									: "Choose the product language Duxly should translate from."
							}
						/>
						<ChecklistItem
							complete={hasTargetLocales}
							label="Target languages"
							detail={
								hasTargetLocales
									? targetProgress
									: targetLimit
										? `Select up to ${targetLimit} target languages for your ${planName} plan.`
										: "Select the storefront languages Duxly should keep translated."
							}
						/>
						<ChecklistItem
							complete={hasEngine}
							label="Translation engine"
							detail={
								hasEngine
									? `${engine} will translate your configured fields.`
									: "Pick DeepL or OpenAI before starting translation runs."
							}
						/>
					</InlineGrid>
				</BlockStack>
			</Card>
		</Layout.Section>
	);
}

export default function Dashboard() {
	const navigate = useNavigate();
	const { usage } = useUsage();
	const [data, setData] = useState<DashboardData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

	async function fetchDashboard() {
		try {
			const res = await authenticatedFetch<DashboardData>("/api/dashboard");
			setData(res);
			setError(null);
		} catch (err: unknown) {
			if (loading) {
				setError(err instanceof Error ? err.message : "Failed to load dashboard");
			}
		}
	}

	useEffect(() => {
		fetchDashboard().finally(() => setLoading(false));
		pollRef.current = setInterval(fetchDashboard, POLL_INTERVAL_MS);
		return () => {
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, []);

	if (loading) {
		return (
			<SkeletonPage title="Dashboard">
				<Layout>
					<Layout.Section>
						<Card>
							<SkeletonBodyText lines={3} />
						</Card>
					</Layout.Section>
					<Layout.Section>
						<InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
							{[...Array(4)].map((_, i) => (
								<Card key={i}>
									<BlockStack gap="200">
										<SkeletonBodyText lines={1} />
										<SkeletonDisplayText size="small" />
									</BlockStack>
								</Card>
							))}
						</InlineGrid>
					</Layout.Section>
					<Layout.Section>
						<Card>
							<SkeletonBodyText lines={6} />
						</Card>
					</Layout.Section>
				</Layout>
			</SkeletonPage>
		);
	}

	const runs: RunEntry[] = data?.runs ?? [];
	const sourceLocale = data?.sourceLocale ?? "";
	const targetLocales = data?.targetLocales ?? [];
	const engine = data?.engine
		? data.engine.charAt(0).toUpperCase() + data.engine.slice(1)
		: "—";
	const configurationIncomplete =
		!sourceLocale || targetLocales.length === 0 || engine === "—";

	const tableRows = runs.map((run) => [
		run.service,
		formatDate(run.lastRun),
		relativeTime(run.lastRun ?? ""),
		formatNumber(run.runCount),
	]);

	return (
		<Page title="Dashboard">
			<Layout>
				<Layout.Section><ProductCounts /></Layout.Section>
				<EntitlementBanner usage={usage} onViewPlans={() => navigate("/plans")} />

				{error && (
					<Layout.Section>
						<Banner tone="critical" title="Failed to load dashboard">
							<p>{error}</p>
						</Banner>
					</Layout.Section>
				)}

				{configurationIncomplete && (
					<ConfigurationOnboarding
						engine={engine}
						sourceLocale={sourceLocale}
						targetLocales={targetLocales}
						planName={usage?.planName}
					/>
				)}

				{/* Translation Overview */}
				<Layout.Section>
					<Card>
						<BlockStack gap="400">
							<InlineStack align="space-between" blockAlign="center" wrap>
								<BlockStack gap="200">
									<Text variant="headingMd" as="h2">
										Translation Overview
									</Text>
									{sourceLocale && (
										<InlineStack gap="200" blockAlign="center">
											<Badge>{localeName(sourceLocale)}</Badge>
											<Text as="span" tone="subdued">
												→
											</Text>
											{targetLocales.map((l) => (
												<Badge key={l} tone="info">
													{localeName(l)}
												</Badge>
											))}
										</InlineStack>
									)}
								</BlockStack>
								<InlineStack gap="200">
									<Tooltip content={RUN_TRANSLATION_TOOLTIP} preferredPosition="above">
										<Button url="/products" variant="primary">
											{data?.triggerMode === "sync-safe" ? "View products" : "Run Translation"}
										</Button>
									</Tooltip>
									<Button url="/configuration">Configure</Button>
								</InlineStack>
							</InlineStack>

							<Divider />

							<InlineStack gap="400" blockAlign="center">
								<BlockStack gap="100">
									<Text as="p" variant="bodySm" tone="subdued">
										Last run
									</Text>
									<InlineStack gap="200" blockAlign="center">
										<Text as="p" variant="bodyMd" fontWeight="semibold">
											{data?.lastRunDate
												? relativeTime(data.lastRunDate)
												: "No runs yet"}
										</Text>
										{data?.lastRunDate && (
											<Text as="p" variant="bodySm" tone="subdued">
												({formatDate(data.lastRunDate)})
											</Text>
										)}
									</InlineStack>
								</BlockStack>
							</InlineStack>
						</BlockStack>
					</Card>
				</Layout.Section>

				{/* Stat Cards */}
				<Layout.Section>
					<InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
						<StatCard
							label="Total Runs"
							value={formatNumber(data?.runCount)}
						/>
						<StatCard
							label="Characters Translated"
							value={formatNumber(data?.totalCharacters)}
						/>
						<StatCard
							label="Target Languages"
							value={String(targetLocales.length)}
						/>
						<StatCard label="Engine" value={engine} />
					</InlineGrid>
				</Layout.Section>

				{/* Recent Runs */}
				<Layout.Section>
					<Card>
						<BlockStack gap="400">
							<Text variant="headingMd" as="h2">
								Recent Runs
							</Text>
							{runs.length === 0 ? (
								<EmptyState
									heading="No translation runs yet"
									action={{
										content: data?.triggerMode === "sync-safe" ? "View products" : "Run your first translation",
										url: "/products",
									}}
									image=""
								>
									<p>
										Start a translation run to see your history
										here.
									</p>
								</EmptyState>
							) : (
								<DataTable
									columnContentTypes={[
										"text",
										"text",
										"text",
										"numeric",
									]}
									headings={[
										"Service",
										"Last Run",
										"",
										"Run Count",
									]}
									rows={tableRows}
								/>
							)}
						</BlockStack>
					</Card>
				</Layout.Section>
			</Layout>
		</Page>
	);
}
